
import { supabase } from '../client';
import { toManualUserId } from '@/types';

/**
 * Find all games that a score is eligible for based on:
 * 1. User is a game member
 * 2. All game members (real + manual) are present in presentPlayerIds
 * 3. Game is at the same course
 * 4. Score date falls within game date range
 *
 * Uses batch queries instead of N sequential per-game queries.
 * presentPlayerIds may include manual_${id} prefixed IDs for manual players.
 */
export const findEligibleGameIds = async ({
  userId,
  courseId,
  scoreDate,
  presentPlayerIds,
  manualPlayerId,
}: {
  userId: string;
  courseId: string;
  scoreDate: string;
  presentPlayerIds: string[];
  manualPlayerId?: string;
}): Promise<string[]> => {
  // 1. Get all games where the effective player is a member
  let userGameIds: string[];

  if (manualPlayerId) {
    // Manual player: look in game_manual_players
    const { data: mpGameLinks } = await supabase
      .from('game_manual_players')
      .select('game_id')
      .eq('manual_player_id', manualPlayerId);
    if (!mpGameLinks || mpGameLinks.length === 0) return [];
    userGameIds = [...new Set(mpGameLinks.map(link => link.game_id))];
  } else {
    // Real user: look in game_players
    const { data: userGameLinks } = await supabase
      .from('game_players')
      .select('game_id')
      .eq('player_id', userId);
    if (!userGameLinks || userGameLinks.length === 0) return [];
    userGameIds = [...new Set(userGameLinks.map(link => link.game_id))];
  }

  // 2. Batch: get ALL real members for all candidate games
  const { data: allMembers } = await supabase
    .from('game_players')
    .select('game_id, player_id')
    .in('game_id', userGameIds);

  if (!allMembers) return [];

  // Group members by game (real profile IDs)
  const membersByGame = new Map<string, string[]>();
  for (const m of allMembers) {
    const members = membersByGame.get(m.game_id) || [];
    members.push(m.player_id);
    membersByGame.set(m.game_id, members);
  }

  // 2b. Also fetch manual members for candidate games
  const { data: manualMembers } = await supabase
    .from('game_manual_players')
    .select('game_id, manual_player_id')
    .in('game_id', userGameIds);

  if (manualMembers) {
    for (const m of manualMembers) {
      const members = membersByGame.get(m.game_id) || [];
      // Use the prefixed ID so it matches presentPlayerIds format
      members.push(toManualUserId(m.manual_player_id));
      membersByGame.set(m.game_id, members);
    }
  }

  // Filter: only games where ALL members are in presentPlayerIds
  const presentSet = new Set(presentPlayerIds);
  const memberMatchedGameIds = userGameIds.filter(gid => {
    const members = membersByGame.get(gid) || [];
    return members.every(pid => presentSet.has(pid));
  });

  if (memberMatchedGameIds.length === 0) return [];

  // 3. Batch: get game details (course_id, dates) for remaining candidates
  const { data: games } = await supabase
    .from('games')
    .select('id, course_id, start_date, end_date')
    .in('id', memberMatchedGameIds);

  if (!games) return [];

  // Filter: same course + date in range
  const scoreDateObj = new Date(scoreDate);
  return games
    .filter(game => {
      if (game.course_id !== courseId) return false;
      if (game.start_date && scoreDateObj < new Date(game.start_date)) return false;
      if (game.end_date && scoreDateObj > new Date(game.end_date)) return false;
      return true;
    })
    .map(game => game.id);
};
